/**
 * Click-ID + UTM capture with 90-day TTL.
 *
 * Captures from URL on every page load:
 *   - utm_source / utm_medium / utm_campaign / utm_content / utm_term
 *   - fbclid (Meta)  → also formatted into the `_fbc` cookie shape per Meta spec
 *   - gclid  (Google Ads)
 *   - ttclid (TikTok)
 *
 * Persists in localStorage under `attribution_v2` (versioned for migrations).
 *   { utm_source, utm_medium, ..., fbclid, gclid, ttclid,
 *     referrer, landing_page, captured_at, expires_at }
 *
 * Behaviour:
 *   - First-touch wins for UTM (write-once until expiry).
 *   - Last-touch wins for click IDs (overwrites on new ad click, which is what
 *     Meta/Google expect for accurate attribution).
 *   - landing_page captured ALWAYS (even on organic landings) on first visit.
 *
 * GDPR: this module ONLY reads URL params. It does NOT send anything.
 * The form submitters decide whether to forward the data to the backend,
 * gated by `consent_marketing` on the form itself.
 */

const STORAGE_KEY = "attribution_v2";
const TTL_DAYS = 90;
const TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"];
const CLICK_ID_KEYS = ["fbclid", "gclid", "ttclid"];

function readStorage() {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw);
    if (obj?.expires_at && Date.now() > obj.expires_at) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return obj;
  } catch {
    return null;
  }
}

function writeStorage(obj) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch { /* private browsing — ignore */ }
}

/**
 * Build the Meta `_fbc` cookie value from a raw `fbclid`.
 * Format: `fb.1.<creation_ts>.<fbclid>` per Meta CAPI docs.
 */
function fbcFromFbclid(fbclid) {
  return `fb.1.${Date.now()}.${fbclid}`;
}

/**
 * Capture URL params and merge with the existing storage entry.
 * Call this on first render + on every route change (idempotent).
 */
export function captureAttribution() {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(window.location.search);
  const existing = readStorage() || {};
  const now = Date.now();

  const next = { ...existing };
  let dirty = false;

  // UTMs — first-touch wins, but only if at least one UTM is present in URL.
  const utmInUrl = UTM_KEYS.some((k) => params.get(k));
  if (utmInUrl) {
    UTM_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v && !next[k]) { next[k] = v; dirty = true; }
    });
  }

  // Click IDs — last-touch wins. New ad click should overwrite old attribution.
  CLICK_ID_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v && v !== next[k]) { next[k] = v; dirty = true; }
  });

  // Synthesize `_fbc` shape so the backend can forward it as-is to Meta.
  if (next.fbclid && !next.fbc) {
    next.fbc = fbcFromFbclid(next.fbclid);
    dirty = true;
  }

  // First-touch: referrer + landing_page.
  if (!next.landing_page) {
    next.landing_page = window.location.pathname + window.location.search;
    dirty = true;
  }
  if (!next.referrer) {
    next.referrer = document.referrer || null;
    dirty = true;
  }

  if (dirty || !existing.captured_at) {
    next.captured_at = existing.captured_at || now;
    next.expires_at = now + TTL_MS;
    writeStorage(next);
  }
  return next;
}

/**
 * Returns the stored attribution snapshot (or null when expired/empty).
 */
export function getAttribution() {
  return readStorage();
}

/**
 * Read Meta Pixel cookies `_fbp` + `_fbc` from document.cookie.
 * Returns `{fbp, fbc}` — either field may be null.
 *
 * `_fbp` is set by Pixel automatically on first load post-consent.
 * `_fbc` is set when URL has `?fbclid=...`; if missing, we fall back to our
 *  synthesized value from the stored attribution.
 */
export function getMetaCookies() {
  if (typeof document === "undefined") return { fbp: null, fbc: null };
  const cookies = document.cookie.split(";").reduce((acc, c) => {
    const [k, ...rest] = c.trim().split("=");
    if (k) acc[k] = rest.join("=");
    return acc;
  }, {});
  const fbp = cookies._fbp || null;
  let fbc = cookies._fbc || null;
  if (!fbc) {
    const attr = readStorage();
    fbc = attr?.fbc || null;
  }
  return { fbp, fbc };
}

/**
 * Read GA4 `_ga` cookie → strip the leading version prefix per MP spec.
 * `_ga=GA1.1.<random>.<ts>` → MP expects `<random>.<ts>`.
 */
export function getGaClientId() {
  if (typeof document === "undefined") return null;
  const raw = document.cookie.split(";").map((c) => c.trim()).find((c) => c.startsWith("_ga="));
  if (!raw) return null;
  const value = raw.split("=")[1];
  const parts = value.split(".");
  // GA1.<version>.<random>.<ts> → last two segments are the client_id.
  if (parts.length >= 4) return `${parts[2]}.${parts[3]}`;
  return null;
}

/**
 * One-liner used by form submitters to assemble the tracking sidecar.
 * Includes the universal `event_id` (uuid v4) for browser↔server dedup.
 */
export function buildTrackingPayload() {
  const attr = captureAttribution() || {};
  const { fbp, fbc } = getMetaCookies();
  const ga_client_id = getGaClientId();
  const event_id = (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return {
    event_id,
    fbclid: attr.fbclid || null,
    gclid: attr.gclid || null,
    ttclid: attr.ttclid || null,
    fbp,
    fbc: fbc || attr.fbc || null,
    ga_client_id,
  };
}
